<template>
  <BaseModal
    v-if="isShowing"
    color="grey-light"
    rounded
    drop-shadow
    size="lg"
    :noPadding="true"
  >
    <article class="profile-company-modal">
      <h1 class="profile-company-modal__title">Company Profile Form</h1>
      <ProgressStepBar
        :steps="steps"
        :currentStep="selectedStep"
        @clicked-step="handleClickedStep"
        class="profile-company-modal__progress-step-bar"
      />
      <span class="spacer" />
      <form
        @submit.prevent="submit()"
        class="profile-company-modal__form"
        v-if="steps.length >= 2"
      >
        <section
          v-for="field in steps[selectedStep].fields"
          :key="`step-field-${field.QuestionID}`"
          class="profile-company-modal__form-group"
        >
          <label
            :for="`question-${field.QuestionID}`"
            class="profile-company-modal__label"
            v-html="field.Question"
          />
          <BaseTextarea
            v-model="field.Answer"
            :id="`question-${field.QuestionID}`"
            placeholder="Enter Notes"
          ></BaseTextarea>
        </section>
        <section class="profile-company-modal__form-button-group">
          <BaseButton
            class="profile-company-modal__form-button"
            :disabled="isSubmitting"
            @click="handleNavigation()"
          >
            {{ selectedStep === steps.length - 1 ? 'SUBMIT' : 'NEXT' }}
          </BaseButton>
          <BaseButton
            color="charcoal"
            class="profile-company-modal__form-button"
            :disabled="isSubmitting"
            @click="completeLater()"
          >
            COMPLETE LATER
          </BaseButton>
        </section>
      </form>
    </article>
    <ItrLoading v-if="isSubmitting" infinite />
  </BaseModal>
</template>

<script>
  import BaseModal from '~/components/base/BaseModal.vue';
  import ProgressStepBar from '~/components/progress/ProgressStepBar.vue';
  import BaseTextarea from '~/components/base/BaseTextarea.vue';
  import BaseButton from '~/components/base/BaseButton.vue';
  import ItrLoading from '~/components/ItrLoading.vue';

  export default {
    name: 'ProfileCompanyModal',
    components: {
      BaseModal,
      ProgressStepBar,
      BaseTextarea,
      BaseButton,
      ItrLoading,
    },
    data: () => ({
      title: 'Company Profile',
      companyProfile: {},
      selectedStep: 1,
      steps: [
        {
          label: 'Company Profile',
          completed: false,
          noninteractive: true,
          fields: [],
        },
      ],
      isShowing: false,
      resolvePromise: undefined,
      rejectPromise: undefined,
      isSubmitting: false,
    }),
    async created() {
      try {
        const questionsPerPage = 3;
        const { data } = await axios.get('/dashboard/datasets/company-profile');
        this.companyProfile = data.Data;
        const arrayPages = this.chunkArray(
          this.companyProfile.Questions,
          questionsPerPage
        );

        if (this.companyProfile.ID != 0) {
          this.selectedStep = this.companyProfile.PageLastOn;
        }

        arrayPages.forEach((page, index) => {
          this.steps.push({
            label: `Page ${index + 1}`,
            completed: false,
            fields: page,
          });
        });
      } catch (error) {
        //
      }
    },
    methods: {
      async submitForm(completeLater = false) {
        this.isSubmitting = true;
        const questions = this.steps.reduce((acc, step) => {
          if (step.noninteractive) return acc;
          return acc.concat(step.fields);
        }, []);
        try {
          const { data } = await axios.post(
            '/dashboard/datasets/company-profile',
            {
              id: this.companyProfile.ID,
              companyId: this.companyProfile.CompanyID,
              completeLater,
              questions,
              selectedStep: this.selectedStep,
            }
          );

          if (!data.IsValid) throw new Error(data.ResponseMessage);
          if (completeLater) {
            this.__cancel();
            return;
          }
          this.__confirm();
        } catch (error) {
          this.__reject();
        } finally {
          this.isSubmitting = false;
        }
      },
      handleNavigation() {
        if (this.selectedStep < this.steps.length - 1) {
          this.selectedStep += 1;
          return;
        }

        this.submitForm();
      },
      completeLater() {
        this.submitForm(true);
        this.$emit('close');
      },
      handleClickedStep(index) {
        this.selectedStep = index;
      },
      chunkArray(array, size) {
        const chunkedArray = [];
        let copied = [...array];
        const numOfChild = Math.ceil(copied.length / size);

        for (let index = 0; index < numOfChild; index++) {
          chunkedArray.push(copied.splice(0, size));
        }
        return chunkedArray;
      },
      prompt() {
        this.isShowing = true;
        return new Promise((resolve, reject) => {
          this.resolvePromise = resolve;
          this.rejectPromise = reject;
        });
      },
      __confirm() {
        this.isShowing = false;
        this.resolvePromise(true);
      },
      __reject() {
        this.isShowing = false;
        this.rejectPromise(true);
      },
      __cancel() {
        this.isShowing = false;
        this.resolvePromise(false);
      },
    },
  };
</script>

<style lang="scss" scoped>
  .profile-company-modal {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 0;
    height: 100%;
    padding: 32px 46px;

    &__title {
      font-size: 26px;
      font-weight: 600;
      text-align: center;
    }

    &__progress-step-bar {
      width: 50%;
    }

    &__form {
      flex: 1 auto;
      gap: 12px;
      width: 100%;
      // height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: space-between;
    }

    &__form-group {
      width: 100%;
    }

    &__form-button-group {
      flex: 1 auto;
      width: 100%;
      display: flex;
      justify-content: center;
      align-items: flex-end;
      gap: 32px;
      margin-top: 16px;
    }

    &__form-button {
      width: 100%;
      max-width: 250px;
      padding: 5px 25px;
    }

    &__label {
      font-size: 14px;
      font-weight: 500;
      color: #333333;
      padding-bottom: 12px;
      margin-bottom: 0;
      width: 100%;
      line-height: 1.4;
    }
  }

  .spacer {
    margin: 15px 0;
    box-sizing: border-box;
    width: 100%;
    height: 1px;
  }
</style>
